import { designVariables } from "@studio3t/design-variables";
import {
  StyledTableScroll,
  styledTableClassNames,
} from "@studio3t/generic-components";
import {
  type Column,
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  type PaginationState,
  type SortingFn,
  type SortingState,
  type Table as TableInstance,
  useReactTable,
} from "@tanstack/react-table";
import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import styled from "styled-components";
import { useDropdownDismissGuard } from "@/components/dropdownDismissGuard";
import { JobStatusBadge } from "@/components/JobStatusBadge";
import { PipelineFlowCell } from "@/components/PipelineFlowCell";
import { SectionCard } from "@/components/SectionCard";
import { SkeletonBar, SkeletonPill } from "@/components/Skeleton";
import { SortableHeader as SharedSortableHeader } from "@/components/SortableHeader";
import { TablePagination } from "@/components/TablePagination";
import type { QueryPhase } from "@/hooks/useApi";
import type { JobMetadata } from "@/types/api";
import { userStorage } from "@/utils/userStorage";
import { deriveJobControlState } from "../pipelineControls";
import {
  formatPipeline,
  type NormalizedJobState,
  normalizeState,
} from "../pipelineHelpers";

const SINGLE_LINE = designVariables.type.singleLine;
const TABLE = designVariables.ui.table;
const PAGE_SIZES = [10, 25, 50, 100];

const columnHelper = createColumnHelper<JobMetadata>();

// Same lifecycle-priority order as the Pipelines list, so ascending (default,
// down-arrow) surfaces running pipelines first.
const STATUS_SORT_ORDER: Record<NormalizedJobState, number> = {
  Running: 0,
  Starting: 1,
  Stopped: 2,
  Stopping: 3,
  Failed: 4,
};

const sortByStatusPriority: SortingFn<JobMetadata> = (rowA, rowB, columnId) => {
  const a = STATUS_SORT_ORDER[rowA.getValue<NormalizedJobState>(columnId)];
  const b = STATUS_SORT_ORDER[rowB.getValue<NormalizedJobState>(columnId)];
  return a - b;
};

// Persist sort + page size per user, under a key distinct from the pipelines
// list so the two tables don't clobber each other.
const TABLE_SETTINGS_KEY = "service-used-by-pipelines-table";
const SORTABLE_COLUMN_IDS = ["name", "pipeline", "status"];

interface StoredSettings {
  pageSize?: number;
  sort?: SortingState;
}

function parseSort(value: unknown): SortingState | undefined {
  if (!Array.isArray(value)) return undefined;
  const sort = value.filter(
    (s): s is { id: string; desc: boolean } =>
      typeof s === "object" &&
      s !== null &&
      typeof (s as { id?: unknown }).id === "string" &&
      SORTABLE_COLUMN_IDS.includes((s as { id: string }).id) &&
      typeof (s as { desc?: unknown }).desc === "boolean",
  );
  return sort.length ? sort : undefined;
}

function loadSettings(): StoredSettings {
  try {
    const raw = userStorage.getItem(TABLE_SETTINGS_KEY);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null) return {};
    const stored = parsed as Record<string, unknown>;
    return {
      pageSize: PAGE_SIZES.includes(stored.pageSize as number)
        ? (stored.pageSize as number)
        : undefined,
      sort: parseSort(stored.sort),
    };
  } catch {
    return {};
  }
}

function saveSettings(settings: StoredSettings): void {
  userStorage.setItem(TABLE_SETTINGS_KEY, JSON.stringify(settings));
}

// Adapts a TanStack column to the shared SortableHeader control (mirrors the
// Pipelines list adapter).
function SortableHeader({
  column,
  children,
}: {
  column: Column<JobMetadata, unknown>;
  children: ReactNode;
}) {
  return (
    <SharedSortableHeader
      direction={column.getIsSorted() || null}
      onClick={() => column.toggleSorting()}
    >
      {children}
    </SharedSortableHeader>
  );
}

function ariaSortFor(
  column: Column<JobMetadata, unknown>,
): "ascending" | "descending" | "none" | undefined {
  if (!column.getCanSort()) return undefined;
  const sorted = column.getIsSorted();
  if (sorted === "asc") return "ascending";
  if (sorted === "desc") return "descending";
  return "none";
}

// Keep the page in range when polling shrinks the row set (autoResetPageIndex
// is off).
function useClampedPageIndex(
  table: TableInstance<JobMetadata>,
  pageIndex: number,
) {
  const pageCount = table.getPageCount();
  useEffect(() => {
    const maxIndex = Math.max(0, pageCount - 1);
    if (pageIndex > maxIndex) table.setPageIndex(maxIndex);
  }, [table, pageCount, pageIndex]);
}

// Skeleton body shown while the jobs query loads. Uses the same row/cell
// components as the data rows so column widths and padding match exactly.
const SKELETON_ROWS = Array.from({ length: 3 }, (_, i) => `skeleton-${i}`);

function SkeletonBodyRows() {
  return (
    <>
      {SKELETON_ROWS.map((key) => (
        <TableRow key={key} aria-hidden="true">
          <TableCell>
            <SkeletonBar $width="70%" />
          </TableCell>
          <TableCell>
            <SkeletonBar $width="55%" />
          </TableCell>
          <StatusCell>
            <SkeletonPill $width="64px" />
          </StatusCell>
        </TableRow>
      ))}
    </>
  );
}

export function UsedByPipelinesTable({
  pipelines,
  phase = "ready",
  onRowClick,
  bare = false,
}: {
  pipelines: JobMetadata[];
  // Lifecycle of the jobs query backing `pipelines`: the body shows skeleton
  // rows while it loads and a failure message if it errors, instead of the
  // "not used" empty state (or a skeleton that would spin forever).
  phase?: QueryPhase;
  onRowClick: (name: string) => void;
  /** Without the titled SectionCard chrome — for the UDF detail's tab
   *  structure, where the tab bar carries the title and the panel the
   *  border (Figma 13984-223188). */
  bare?: boolean;
}) {
  const loading = phase === "loading";
  const stored = useMemo(() => loadSettings(), []);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: stored.pageSize ?? 10,
  });
  const [sorting, setSorting] = useState<SortingState>(() => stored.sort ?? []);

  const columns = useMemo(
    () => [
      columnHelper.accessor("name", {
        header: "Name",
        cell: (info) => <PipelineName>{info.getValue()}</PipelineName>,
      }),
      // Sortable by source (service.resource) only — the flow's natural sort
      // key, matching the Pipelines list.
      columnHelper.accessor((job) => formatPipeline(job).source, {
        id: "pipeline",
        header: "Pipeline",
        cell: (info) => <PipelineFlowCell job={info.row.original} />,
      }),
      columnHelper.accessor(
        (job) =>
          deriveJobControlState(
            normalizeState(job.state),
            normalizeState(job.desired_state),
          ).displayState,
        {
          id: "status",
          header: "Status",
          sortingFn: sortByStatusPriority,
          cell: (info) => (
            <StatusSlot>
              <JobStatusBadge state={info.getValue()} />
            </StatusSlot>
          ),
        },
      ),
    ],
    [],
  );

  const table = useReactTable({
    data: pipelines,
    columns,
    state: { pagination, sorting },
    onPaginationChange: setPagination,
    onSortingChange: (updater) => {
      setSorting(updater);
      // Sorting reorders the whole set; jump back to page 1 so the new top rows
      // are in view (autoResetPageIndex is off for polling).
      setPagination((p) => ({ ...p, pageIndex: 0 }));
    },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getRowId: (job) => job.name,
    autoResetPageIndex: false,
  });

  useClampedPageIndex(table, pagination.pageIndex);

  // Only persist genuine user changes — skip the initial mount so a default
  // (e.g. no sort) is never baked into storage and re-applied on later loads.
  const didMountRef = useRef(false);
  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }
    saveSettings({ pageSize: pagination.pageSize, sort: sorting });
  }, [pagination.pageSize, sorting]);

  // No column filters on this table, so the total is just the row count.
  const total = pipelines.length;

  // Footer page navigation (first/prev/next/last) brings the table back to the
  // top: reset the table's own scroll and scroll the page so the card sits just
  // below the sticky page header.
  const anchorRef = useRef<HTMLDivElement>(null);
  const scrollToTableTop = useCallback(() => {
    const anchor = anchorRef.current;
    if (!anchor) return;
    anchor
      .querySelector<HTMLElement>("[data-overlayscrollbars-viewport]")
      ?.scrollTo({ top: 0 });
    const scroller = anchor.closest<HTMLElement>(
      "[data-overlayscrollbars-viewport]",
    );
    if (!scroller) {
      anchor.scrollIntoView({ block: "start", behavior: "smooth" });
      return;
    }
    const header = scroller.querySelector<HTMLElement>(
      "[data-sticky-page-header]",
    );
    // Leave a small gap below the sticky header so the card's top border is
    // visible rather than flush against it.
    const gap = designVariables.ui.semantic.spacings;
    const delta =
      anchor.getBoundingClientRect().top -
      scroller.getBoundingClientRect().top -
      (header?.offsetHeight ?? 0) -
      gap;
    scroller.scrollBy({ top: delta, behavior: "smooth" });
  }, []);

  // A click that closes the page-size menu does nothing else.
  const dismissGuard = useDropdownDismissGuard<HTMLDivElement>();
  const tableBody = (
    <TableWrap
      $bare={bare}
      ref={dismissGuard.containerRef}
      onClickCapture={dismissGuard.onClickCapture}
    >
      <StyledTableScroll>
        <Table>
          {/* 240 mirrors the Pipelines list Name column; 120 the Status
                  column; the Pipeline column takes the remaining space. */}
          <colgroup>
            <col style={{ width: 240 }} />
            <col />
            <col style={{ width: 120 }} />
          </colgroup>
          <TableHead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHeader
                    key={header.id}
                    aria-sort={ariaSortFor(header.column)}
                  >
                    {header.column.getCanSort() ? (
                      <SortableHeader column={header.column}>
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                      </SortableHeader>
                    ) : (
                      flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )
                    )}
                  </TableHeader>
                ))}
              </tr>
            ))}
          </TableHead>
          <tbody>
            {loading && <SkeletonBodyRows />}
            {!loading &&
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  tabIndex={0}
                  onClick={() => onRowClick(row.original.name)}
                  onKeyDown={(e) => {
                    if (e.target !== e.currentTarget) return;
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      onRowClick(row.original.name);
                    }
                  }}
                >
                  {row.getVisibleCells().map((cell) => {
                    const Cell =
                      cell.column.id === "status" ? StatusCell : TableCell;
                    return (
                      <Cell key={cell.id}>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </Cell>
                    );
                  })}
                </TableRow>
              ))}
          </tbody>
        </Table>

        {/* Headers stay visible with a placeholder below, like the Services
                list empty state. An errored jobs query means the pipeline list
                is unknown, not empty — say so instead of "not used". */}
        {phase === "error" && total === 0 && (
          <EmptyRowMessage>Failed to load pipelines</EmptyRowMessage>
        )}
        {phase === "ready" && total === 0 && (
          <EmptyRowMessage>Not used by any pipelines</EmptyRowMessage>
        )}
      </StyledTableScroll>

      {/* Page once there are more than a full page (10) of pipelines. */}
      {total > PAGE_SIZES[0] && (
        <TablePagination
          pageIndex={pagination.pageIndex}
          pageCount={table.getPageCount()}
          pageSize={pagination.pageSize}
          total={total}
          onPageChange={(index) => {
            table.setPageIndex(index);
            scrollToTableTop();
          }}
          onPageSizeChange={(size) => {
            table.setPageSize(size);
            table.setPageIndex(0);
          }}
          onOpenChange={dismissGuard.onDropdownOpenChange}
        />
      )}
    </TableWrap>
  );

  return (
    <div ref={anchorRef}>
      {bare ? (
        tableBody
      ) : (
        <SectionCard title="Used by pipelines">{tableBody}</SectionCard>
      )}
    </div>
  );
}

// StyledTableScroll draws its own frame/border; the SectionCard already owns the
// border, so strip the frame and let the rows hug the pagination bar. The
// border-top is the header/table divider (Figma card header border-bottom).
const TableWrap = styled.div<{ $bare?: boolean }>`
  display: flex;
  flex-direction: column;
  min-height: 0;
  /* In the tab structure the bar's bottom border already separates the
     table; only the titled card needs its header/table divider. */
  border-top: ${(props) =>
    props.$bare ? "none" : `1px solid ${props.theme.cards1Border}`};

  .${styledTableClassNames.scroll} {
    border: none;
    height: auto;
    min-height: 0;
  }

  /* StyledTableScroll defaults to overscroll-behavior: none, which traps the
     wheel here. This table only scrolls horizontally (and only when narrow), so
     let a vertical wheel chain to the page scroll instead of dead-ending. */
  [data-overlayscrollbars-viewport] {
    overscroll-behavior: auto !important;
  }

  .os-scrollbar-vertical {
    top: ${TABLE.thead.cell.height + 1}px !important;
  }
`;

const Table = styled.table`
  width: 100%;
  /* Name (240) + Pipeline flow (~560 worst case before ellipsing) + Status
     (120); below this the card scrolls horizontally instead of squashing. */
  min-width: 920px;
  border-collapse: separate;
  border-spacing: 0;
  table-layout: fixed;
  background: ${(props) => props.theme.tableTheadCellBackground};

  tbody tr:last-child td {
    border-bottom: none;
  }

  th:last-child,
  td:last-child {
    border-right: none;
  }
`;

const TableHead = styled.thead`
  background: ${(props) => props.theme.tableTheadCellBackground};

  th {
    position: sticky;
    top: 0;
    z-index: 1;
    background: ${(props) => props.theme.tableTheadCellBackground};
  }
`;

const TableRow = styled.tr`
  cursor: pointer;

  &:hover {
    background: ${(props) => props.theme.tableTbodyCellHoverBackground};
  }

  &:focus-visible {
    outline-offset: -${designVariables.ui.semantic.focusstroke}px;
  }
`;

const TableHeader = styled.th`
  text-align: left;
  height: ${TABLE.thead.cell.height}px;
  padding: 0 ${TABLE.thead.cell.paddingr}px 0 ${TABLE.thead.cell.paddingl}px;
  font-size: ${SINGLE_LINE.small.regular.fontSize}px;
  font-weight: ${SINGLE_LINE.small.regular.fontWeight};
  color: ${(props) => props.theme.semanticTextLight};
  white-space: nowrap;
  border-bottom: 1px solid ${(props) => props.theme.tableBackground};
  border-right: 1px solid ${(props) => props.theme.tableBackground};
`;

const TableCell = styled.td`
  height: ${TABLE.tbody.cell.heightm}px;
  padding: 0 ${TABLE.tbody.cell.padding}px;
  font-size: ${SINGLE_LINE.default.regular.fontSize}px;
  color: ${(props) => props.theme.semanticTextBody};
  vertical-align: middle;
  border-bottom: 1px solid ${(props) => props.theme.tableBackground};
  border-right: 1px solid ${(props) => props.theme.tableBackground};
  overflow: hidden;
`;

const StatusCell = styled(TableCell)``;

const CellText = styled.span`
  display: block;
  padding: 0 ${TABLE.tbody.cell.gap}px;
  color: ${(props) => props.theme.semanticTextBody};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const PipelineName = styled(CellText)``;

const StatusSlot = styled.div`
  padding-left: ${TABLE.tbody.cell.gap}px;

  & > * {
    display: inline-flex;
  }
`;

// Matches the Services / Pipelines list empty state: centered italic message
// under the (still visible) column headers.
const EmptyRowMessage = styled.div`
  padding: 40px 0;
  text-align: center;
  font-size: ${SINGLE_LINE.small.regular.fontSize}px;
  font-style: italic;
  color: ${(props) => props.theme.semanticTextLight};
`;
